import { useState } from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Card, CardContent } from '@/components/ui/card'
import { useDinSettings, useSetDinSettings } from '@/hooks/useProducts'
import type { DinSettings } from '@/api/productsApi'
import { DIN_FLAG_PATTERN, FACILITY_ID_PATTERN, formatDinForLabel } from '@/lib/isbtDin'

/**
 * The facility ID this browser kept before the settings moved to the server
 * (z8nrz7fxwh). Offered once, as the starting value, so nobody has to look it
 * up again; never written back.
 */
function legacyFacilityId(): string {
  try {
    return (localStorage.getItem('cgt.din.facilityId') ?? '').trim().toUpperCase()
  } catch {
    return ''
  }
}

/**
 * Configure → DIN Configure: the tenant's ISBT 128 DIN settings (z8nrz7fxwh).
 * Every DIN minted in this tenant is facility ID + 2-digit year + 6-digit
 * sequence, with the flag and check character printed after it.
 */
export function DinConfigureSettings() {
  const settings = useDinSettings()

  if (settings.isLoading) return <p className='text-sm text-muted-foreground'>Loading DIN settings…</p>
  if (settings.isError || !settings.data) {
    return <p className='text-sm text-destructive'>Could not load the DIN settings.</p>
  }
  // Not keyed on the data: a refetch (the sequence moved) must not wipe an
  // edit in progress. A tenant switch remounts the whole route anyway.
  return <DinSettingsForm saved={settings.data} />
}

function DinSettingsForm({ saved }: { saved: DinSettings }) {
  const save = useSetDinSettings()
  const legacy = saved.configured ? '' : legacyFacilityId()
  const [facilityId, setFacilityId] = useState(saved.configured ? saved.facilityId : legacy)
  const [flag, setFlag] = useState(saved.flag)
  const [startingNumber, setStartingNumber] = useState(String(saved.startingNumber))
  const [justSaved, setJustSaved] = useState(false)

  const facilityOk = FACILITY_ID_PATTERN.test(facilityId)
  const flagOk = DIN_FLAG_PATTERN.test(flag)
  const start = Number(startingNumber)
  const startOk = startingNumber.trim() !== '' && Number.isInteger(start) && start >= 0 && start <= 999_999

  function handleSave() {
    setJustSaved(false)
    save.mutate(
      { facilityId, flag, startingNumber: start },
      { onSuccess: () => setJustSaved(true) },
    )
  }

  return (
    <div className='space-y-4'>
      <div>
        <h2 className='text-base font-semibold text-foreground'>DIN Configuration</h2>
        <p className='text-sm text-muted-foreground'>
          Every product made in this facility gets an ISBT 128 DIN: the Facility ID, the 2-digit
          year and a 6-digit sequence number, 13 characters with no hyphens. The flag and a
          check character are printed after it.
        </p>
      </div>

      <Card>
        <CardContent className='space-y-4 pt-6'>
          {!saved.configured && (
            <p className='rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900 dark:border-amber-700 dark:bg-amber-950 dark:text-amber-200'>
              Not set up yet. Products cannot be created in this facility until a Facility ID is saved.
              {legacy && ' The Facility ID below is the one this browser used before.'}
            </p>
          )}

          <div className='grid max-w-md gap-4'>
            <div className='space-y-1'>
              <label className='text-sm font-medium' htmlFor='din-facility-id'>Facility ID</label>
              <Input
                id='din-facility-id'
                value={facilityId}
                onChange={e => setFacilityId(e.target.value.trim().toUpperCase())}
                placeholder='e.g. A9999'
                maxLength={5}
                className='font-mono'
              />
              <p className={facilityOk || !facilityId ? 'text-xs text-muted-foreground' : 'text-xs text-destructive'}>
                A letter (not O) and four digits, the facility's ISBT 128 identification number.
              </p>
            </div>

            <div className='space-y-1'>
              <label className='text-sm font-medium' htmlFor='din-flag'>Flag</label>
              <Input
                id='din-flag'
                value={flag}
                onChange={e => setFlag(e.target.value.trim())}
                placeholder='00'
                maxLength={2}
                className='w-20 font-mono'
              />
              <p className={flagOk ? 'text-xs text-muted-foreground' : 'text-xs text-destructive'}>
                Two digits printed after every DIN. 00 means no flag; 01 and 02 mark container 1 and 2 of a
                set; 20–59 are for this facility to define.
              </p>
            </div>

            <div className='space-y-1'>
              <label className='text-sm font-medium' htmlFor='din-starting-number'>Starting Number</label>
              <Input
                id='din-starting-number'
                type='number'
                min={0}
                max={999999}
                value={startingNumber}
                onChange={e => setStartingNumber(e.target.value)}
                placeholder='1'
              />
              <p className={startOk ? 'text-xs text-muted-foreground' : 'text-xs text-destructive'}>
                The sequence starts here (0–999999). Raising it moves the next number up; it does not
                restart each year.
              </p>
            </div>

            {saved.configured && (
              <div className='space-y-1'>
                <span className='text-sm font-medium'>Next DIN</span>
                <p className='rounded-md bg-muted px-3 py-2 font-mono text-sm' data-testid='din-next'>
                  {saved.nextDin ? formatDinForLabel(saved.nextDin, saved.flag) : 'Sequence used up'}
                </p>
                <p className='text-xs text-muted-foreground'>
                  Sequence number {saved.nextSequence}. It advances with each product created, for every
                  user in this facility.
                </p>
              </div>
            )}
          </div>

          <div className='flex items-center gap-3'>
            <Button onClick={handleSave} disabled={!facilityOk || !flagOk || !startOk || save.isPending}>
              {save.isPending ? 'Saving…' : 'Save'}
            </Button>
            {justSaved && <span className='text-sm text-green-600'>Saved</span>}
            {save.isError && (
              <span className='text-sm text-destructive'>
                {save.error instanceof Error ? save.error.message : 'Could not save.'}
              </span>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
